/**
 * Asset Policies admin page
 *
 * CRUD over /api/media-library/policies — per-org rules that decide:
 *  - which media asset types must expire
 *  - the default expiration window
 *  - whether expired assets can still be used in communications
 *  - whether new assets in this category require approval
 *
 * Resolution order at runtime: asset override → policy by (asset_type, category)
 * → policy by asset_type only → org default. See media_compliance_service.py.
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ShieldAlert, Info } from 'lucide-react';

interface Props {
  /** When set, renders a back link instead of standalone-page header. */
  onBack?: () => void;
  /** Override the hook-derived isRegulated value (parent has the authoritative one from /api/compliance/settings). */
  isRegulatedOverride?: boolean;
}
import {
  contentComplianceAPI,
  AssetTypePolicy,
  AssetTypePolicyUpsert,
} from '@/services/api/contentCompliance';
import { useComplianceMode } from '@/hooks/useComplianceMode';

const ASSET_TYPES = ['document', 'image', 'video', 'audio', 'web_link', 'artifact'] as const;
const EXPIRATION_POLICIES: Array<{ value: 'never' | 'always' | 'sometimes'; label: string; help: string }> = [
  { value: 'never', label: 'Never expires', help: 'Assets in this category have no expiration date.' },
  { value: 'always', label: 'Always expires', help: 'Expiration date is required. Sends block automatically once past the date.' },
  { value: 'sometimes', label: 'Optional expiration', help: 'Expiration date may be set per asset; rules apply only when set.' },
];

const POLICY_BADGE: Record<string, string> = {
  always: 'bg-amber-100 text-amber-900',
  sometimes: 'bg-blue-100 text-blue-900',
  never: 'bg-paper-2 text-ink-700',
};

interface FormState extends AssetTypePolicyUpsert {
  // local UI mirror of the backend payload
}

const EMPTY_FORM: FormState = {
  asset_type: 'document',
  regulated_category: '',
  expiration_policy: 'sometimes',
  default_expiration_days: 365,
  allow_use_after_expiry: false,
  notify_days_before: 30,
  requires_approval: false,
  is_active: true,
};

export default function AssetPoliciesPage({ onBack, isRegulatedOverride }: Props = {}) {
  const { isRegulated: hookRegulated, canManageCompliance } = useComplianceMode();
  const isRegulated = isRegulatedOverride ?? hookRegulated;
  const [policies, setPolicies] = useState<AssetTypePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AssetTypePolicy | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await contentComplianceAPI.listAssetTypePolicies();
      setPolicies(list);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (policy: AssetTypePolicy) => {
    setForm({
      asset_type: policy.asset_type,
      regulated_category: policy.regulated_category || '',
      expiration_policy: policy.expiration_policy,
      default_expiration_days: policy.default_expiration_days,
      allow_use_after_expiry: policy.allow_use_after_expiry,
      notify_days_before: policy.notify_days_before,
      requires_approval: policy.requires_approval,
      is_active: policy.is_active,
    });
    setEditing(policy);
    setCreating(true);
  };

  const closeModal = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: AssetTypePolicyUpsert = {
        ...form,
        regulated_category: form.regulated_category?.trim() ? form.regulated_category.trim() : null,
        default_expiration_days:
          form.expiration_policy === 'never' ? null : form.default_expiration_days ?? null,
      };
      await contentComplianceAPI.upsertAssetTypePolicy(payload);
      closeModal();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (policy: AssetTypePolicy) => {
    if (!confirm(`Remove policy for ${policy.asset_type}${policy.regulated_category ? ` · ${policy.regulated_category}` : ''}?`)) {
      return;
    }
    setDeletingId(policy.id);
    try {
      await contentComplianceAPI.deleteAssetTypePolicy(policy.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, AssetTypePolicy[]>();
    for (const p of policies) {
      const list = map.get(p.asset_type) || [];
      list.push(p);
      map.set(p.asset_type, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [policies]);

  // When reached via the Compliance tab (onBack provided), the parent already
  // gated visibility — skip the redundant permission check here.
  if (!onBack && !canManageCompliance) {
    return (
      <div className="container mx-auto p-8 max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            You don't have permission to manage asset policies.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={onBack ? 'space-y-6' : 'container mx-auto py-8 px-4 max-w-6xl space-y-6'}>
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-700 mb-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Compliance Settings
        </button>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" /> Asset Policies
          </h1>
          <p className="text-sm text-ink-500 mt-1 max-w-2xl">
            Rules per media asset type and regulated category. They decide when assets must expire,
            whether expired content can still be sent, and whether new assets in a category need
            compliance approval before use.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" />
          New policy
        </Button>
      </div>

      {!isRegulated && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This org is in <strong>Standard mode</strong> — policies still resolve and are surfaced
            in the UI, but blocks are advisory. Switch to Regulated Entity mode in Organization
            Settings for enforcement.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
            Loading policies…
          </CardContent>
        </Card>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            No asset policies yet. Click <strong>New policy</strong> to create the first one.
          </CardContent>
        </Card>
      ) : (
        grouped.map(([assetType, items]) => (
          <Card key={assetType}>
            <CardHeader>
              <CardTitle className="text-base capitalize">
                {assetType}{' '}
                <span className="text-xs font-normal text-ink-500">
                  ({items.length} {items.length === 1 ? 'policy' : 'policies'})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-paper text-left text-xs uppercase">
                  <tr>
                    <th className="p-3">Category</th>
                    <th className="p-3">Expiration</th>
                    <th className="p-3">Default window</th>
                    <th className="p-3">Use after expiry</th>
                    <th className="p-3">Notify (days)</th>
                    <th className="p-3">Approval</th>
                    <th className="p-3">Active</th>
                    <th className="p-3 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="p-3 font-medium">
                        {p.regulated_category || (
                          <span className="text-ink-400 italic">all (default)</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge className={POLICY_BADGE[p.expiration_policy] || 'bg-paper-2'}>
                          {p.expiration_policy}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {p.default_expiration_days != null ? `${p.default_expiration_days}d` : '—'}
                      </td>
                      <td className="p-3">{p.allow_use_after_expiry ? 'Allowed' : 'Blocked'}</td>
                      <td className="p-3">{p.notify_days_before}</td>
                      <td className="p-3">
                        {p.requires_approval ? (
                          <Badge className="bg-blue-100 text-blue-900">Required</Badge>
                        ) : (
                          <span className="text-ink-400">No</span>
                        )}
                      </td>
                      <td className="p-3">
                        {p.is_active ? (
                          <Badge className="bg-green-100 text-green-900">Active</Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={deletingId === p.id}
                          onClick={() => remove(p)}
                        >
                          {deletingId === p.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={creating} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit asset policy' : 'New asset policy'}</DialogTitle>
            <DialogDescription>
              Policies are matched first by exact (asset_type, category), then by asset_type only,
              then fall back to org defaults.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Asset type</Label>
                <Select
                  value={form.asset_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, asset_type: v }))}
                  disabled={!!editing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Regulated category (optional)</Label>
                <Input
                  placeholder="e.g. insurance_proof"
                  value={form.regulated_category || ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, regulated_category: e.target.value }))
                  }
                  disabled={!!editing}
                />
                <p className="text-xs text-ink-500 mt-1">
                  Leave blank to apply this policy to every asset of the chosen type.
                </p>
              </div>
            </div>

            <div>
              <Label className="text-sm">Expiration policy</Label>
              <Select
                value={form.expiration_policy}
                onValueChange={(v: any) => setForm((f) => ({ ...f, expiration_policy: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRATION_POLICIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-ink-500 mt-1">
                {EXPIRATION_POLICIES.find((p) => p.value === form.expiration_policy)?.help}
              </p>
            </div>

            {form.expiration_policy !== 'never' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Default expiration window (days)</Label>
                  <Input
                    type="number"
                    value={form.default_expiration_days ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        default_expiration_days:
                          e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-sm">Notify days before</Label>
                  <Input
                    type="number"
                    value={form.notify_days_before ?? 30}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, notify_days_before: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <SwitchRow
                label="Allow use after expiration"
                help="When on, expired assets in this category can still be referenced in communications. Off recommended for regulated content."
                checked={!!form.allow_use_after_expiry}
                onChange={(v) => setForm((f) => ({ ...f, allow_use_after_expiry: v }))}
              />
              <SwitchRow
                label="New assets require approval"
                help="Newly uploaded assets in this category sit in pending_approval until reviewed."
                checked={!!form.requires_approval}
                onChange={(v) => setForm((f) => ({ ...f, requires_approval: v }))}
              />
              <SwitchRow
                label="Policy active"
                help="Disable to keep the rule but stop applying it without losing the configuration."
                checked={form.is_active !== false}
                onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || !form.asset_type}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Create policy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwitchRow({
  label,
  help,
  checked,
  onChange,
}: {
  label: string;
  help: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-ink-500 mt-1">{help}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
